import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from '@/hooks/use-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save } from 'lucide-react';
import ContractVariables from '@/components/ContractVariables';

interface TipoContrato {
  id: string;
  nome: string;
}

interface ModeloContrato {
  id: string;
  nome_modelo: string;
  tipo_contrato_id: string;
  conteudo_html: string;
  status: string;
}

export default function ModeloContratoForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { hasPermission, refetch } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tiposContrato, setTiposContrato] = useState<TipoContrato[]>([]);
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  const [formData, setFormData] = useState({
    nome_modelo: '',
    tipo_contrato_id: '',
    conteudo_html: ''
  });
  const [quillRef, setQuillRef] = useState<any>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const isEditing = !!id;
  const canWrite = hasPermission('recursos_humanos', 'modelos_contrato', 'write');

  console.log('ModeloContratoForm - Permissions:', { canWrite, isEditing, id, permissionsChecked });

  useEffect(() => {
    // Primeiro refetch das permissões
    refetch().then(() => {
      setPermissionsChecked(true);
    });
  }, []);

  useEffect(() => {
    if (!permissionsChecked) return;
    
    if (!canWrite) {
      console.log('ModeloContratoForm - No write permission, redirecting...');
      navigate('/intranet/recursos-humanos/modelos-contrato');
      return;
    }
    
    fetchTiposContrato();
    
    if (isEditing) {
      fetchModelo();
    }
  }, [isEditing, canWrite, permissionsChecked]);

  const fetchTiposContrato = async () => {
    try {
      const { data, error } = await supabase
        .from('tipo_contrato')
        .select('id, nome')
        .eq('status', 'ativo')
        .order('nome');

      if (error) throw error;

      setTiposContrato(data || []);
    } catch (error) {
      console.error('Erro ao buscar tipos de contrato:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar tipos de contrato',
        variant: 'destructive'
      });
    }
  };

  const fetchModelo = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tbl_modelo_contrato')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setFormData({
          nome_modelo: data.nome_modelo,
          tipo_contrato_id: data.tipo_contrato_id,
          conteudo_html: data.conteudo_html
        });
      }
    } catch (error) {
      console.error('Erro ao buscar modelo:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar modelo de contrato',
        variant: 'destructive'
      });
      navigate('/intranet/recursos-humanos/modelos-contrato');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.nome_modelo.trim()) {
      toast({
        title: 'Erro',
        description: 'Nome do modelo é obrigatório',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.tipo_contrato_id) {
      toast({
        title: 'Erro',
        description: 'Tipo de contrato é obrigatório',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.conteudo_html.trim()) {
      toast({
        title: 'Erro',
        description: 'Conteúdo do modelo é obrigatório',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome_modelo: formData.nome_modelo.trim(),
        tipo_contrato_id: formData.tipo_contrato_id,
        conteudo_html: formData.conteudo_html,
        ...(isEditing ? { updated_by: user?.id } : { created_by: user?.id })
      };

      let error;
      if (isEditing) {
        ({ error } = await supabase
          .from('tbl_modelo_contrato')
          .update(payload)
          .eq('id', id));
      } else {
        ({ error } = await supabase
          .from('tbl_modelo_contrato')
          .insert([payload]));
      }

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: `Modelo de contrato ${isEditing ? 'atualizado' : 'criado'} com sucesso`
      });

      navigate('/intranet/recursos-humanos/modelos-contrato');
    } catch (error) {
      console.error('Erro ao salvar modelo:', error);
      toast({
        title: 'Erro',
        description: `Erro ao ${isEditing ? 'atualizar' : 'criar'} modelo de contrato`,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    console.log('Drop event triggered', { isEditing, quillRef: !!quillRef });
    
    const text = e.dataTransfer.getData('text/plain');
    console.log('Dragged text:', text);
    
    if (text && quillRef) {
      const quill = quillRef.getEditor();
      const range = quill.getSelection();
      const index = range ? range.index : quill.getLength();
      
      console.log('Inserting at index:', index);
      quill.insertText(index, text);
      quill.setSelection(index + text.length);
      console.log('Text inserted successfully');
    } else {
      console.log('Missing requirements:', { text: !!text, quillRef: !!quillRef });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  // Configurar a referência do Quill
  const handleQuillRef = (ref: any) => {
    console.log('Quill ref set:', !!ref, 'isEditing:', isEditing);
    setQuillRef(ref);
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link', 'image'
  ];

  if (!canWrite) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Acesso Negado</CardTitle>
          <CardDescription>
            Você não tem permissão para criar/editar modelos de contrato.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        Carregando...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/intranet/recursos-humanos/modelos-contrato')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {isEditing ? 'Editar Modelo' : 'Novo Modelo'} de Contrato
            </h1>
            <p className="text-muted-foreground">
              {isEditing ? 'Atualize as informações do modelo' : 'Crie um novo modelo de contrato'}
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
              <CardDescription>
                Dados principais do modelo de contrato
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome_modelo">Nome do Modelo *</Label>
                <Input
                  id="nome_modelo"
                  value={formData.nome_modelo}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome_modelo: e.target.value }))}
                  placeholder="Digite o nome do modelo"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tipo_contrato_id">Tipo de Contrato *</Label>
                <Select
                  value={formData.tipo_contrato_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, tipo_contrato_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo de contrato" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposContrato.map((tipo) => (
                      <SelectItem key={tipo.id} value={tipo.id}>
                        {tipo.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          
          <ContractVariables />
        </div>

        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Conteúdo do Contrato</CardTitle>
              <CardDescription>
                Use o editor abaixo para criar o modelo do contrato com formatação rica
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Conteúdo *</Label>
                <div 
                  className={`min-h-[400px] transition-all duration-200 ${isDragOver ? 'ring-2 ring-primary ring-opacity-50 bg-primary/5' : ''}`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <ReactQuill
                    ref={handleQuillRef}
                    theme="snow"
                    value={formData.conteudo_html}
                    onChange={(value) => setFormData(prev => ({ ...prev, conteudo_html: value }))}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ height: '350px' }}
                    placeholder="Digite o conteúdo do modelo de contrato ou arraste variáveis aqui..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}